import { queryKeys } from '@query/keys'
import { PaginatedQueryResult, usePaginatedQuery } from '@query/usePaginatedQuery'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { PageQuery, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { FilterQueryProps } from '@model/FilterFields'
import { NameDto } from '@model/NameDto'

export type TradingAccountsRebateSortOptions =
  | 'TradingAccountId'
  | 'TradingAccountName'
  | 'TradingAccountTypeId'
  | 'TradingAccountTypeName'
  | 'TradingAccountSignedUpDate'
  | 'ClientId'
  | 'ClientName'
  | 'CountryId'
  | 'CountryName'
  | 'RebateApprovalTypeName'
  | 'RebatePeriodName'
  | 'RebateGroupId'
  | 'RebateGroupName'
  | 'TotalVolumeFxm'
  | 'TotalVolumeOther'
  | 'TotalCommission'
  | 'RebateAmountInPercentage'

interface IBTradingAccountsRebate {
  query: PaginatedQueryResult<
    import('@model/TradingAccountRebateDetailedDto').TradingAccountRebateDetailedDto
  >
  sortOptions: NameDto<TradingAccountsRebateSortOptions | null>[]
  search?: FilterQueryProps
  setSearch: (search: FilterQueryProps) => void
  forceRefresh: () => void
  clearSearch: (callback: () => void) => void
}

export const useIBTradingAccountsRebate = (overrides?: PageQuery): IBTradingAccountsRebate => {
  const { t } = useTranslation()

  const apiClient = useApiClient(ClientApiClient)
  const locale = useSessionLanguage()
  const { account } = useAccountReadContext()

  const [search, setSearch] = useState<FilterQueryProps>()

  const sortOptions: NameDto<TradingAccountsRebateSortOptions | null>[] = [
    { id: 'TradingAccountName', name: t('IB.RebatesPage.ClientsPage.Trading account') },
    { id: 'ClientName', name: t('IB.RebatesPage.ClientsPage.Client Name') },
    { id: 'TradingAccountTypeName', name: t('IB.RebatesPage.ClientsPage.Account Type') },
    { id: 'TradingAccountSignedUpDate', name: t('IB.RebatesPage.Signed up on') },
    { id: 'CountryName', name: t('IB.RebatesPage.ClientsPage.Country') },
    { id: 'RebateApprovalTypeName', name: t('IB.RebatesPage.ClientsPage.Rebate type') },
    { id: 'RebatePeriodName', name: t('IB.RebatesPage.ClientsPage.Payout frequency') },
    { id: 'RebateGroupName', name: t('IB.RebatesPage.ClientsPage.Group') },
    { id: 'TotalVolumeFxm', name: t('IB.RebatesPage.ClientsPage.Vol. FXM') },
    { id: 'TotalVolumeOther', name: t('IB.RebatesPage.ClientsPage.Vol. Other') },
    { id: 'TotalCommission', name: t('IB.RebatesPage.ClientsPage.Commission') },
    { id: 'RebateAmountInPercentage', name: t('IB.RebatesPage.Rebates') },
  ]

  const clearSearch = (callback: () => void) => {
    callback()
    setSearch({})
  }

  const ibId = account?.clientIntroducingBroker?.id

  const query = usePaginatedQuery({
    queryKey: queryKeys.ib.rebates.clients(ibId, { ...overrides, search, locale } as any),
    queryFn: (pageQuery) =>
      apiClient.getIBTradingAccountsRebate(ibId ?? '', {
        ...pageQuery,
        ...overrides,
        search,
        caller: 'ca',
        languageId: locale,
      }),
  })

  return {
    query,
    sortOptions,
    search,
    setSearch,
    forceRefresh: query.forceRefresh,
    clearSearch,
  }
}
